import { ColDef, GridOptions } from 'ag-grid-community'
import { PendingCollateralDetail } from 'api'
import { Locale, Translation } from 'locale'
import { gridOptionsBase, StatusCellRenderer, TypedValueGetterParams, ValidationCellRenderer } from 'view/components/datagrid'
import {
  collateralIdentifierTypeFormatter,
  collateralTypeFormatter,
  couponFrequencyFormatter,
  couponTypeFormatter,
  getCollateralStatusActiveSuspendedText,
  getCollateralStatusColor,
  indexLagFormatter,
  makeDateFormatter,
  makeNumberFormatter,
  tradingTypeFormatter
} from '../datagrid'
import * as constants from 'view/styles/constants'

enum PendingCollateralGridColumnId {
  COLLATERAL_TRADING = 'COLLATERAL_TRADING',
  COLLATERAL_PLATFORM = 'COLLATERAL_PLATFORM',
  COLLATERAL_NAME = 'COLLATERAL_NAME',
  COLLATERAL_ISIN = 'COLLATERAL_ISIN',
  COLLATERAL_FISN = 'COLLATERAL_FISN',
  COLLATERAL_CFI = 'COLLATERAL_CFI',
  COLLATERAL_CUSIP = 'COLLATERAL_CUSIP',
  COLLATERAL_CURRENCY = 'COLLATERAL_CURRENCY',
  COLLATERAL_COUNTRY = 'COLLATERAL_COUNTRY',
  COLLATERAL_SECTOR = 'COLLATERAL_SECTOR',
  COLLATERAL_PRODUCT = 'COLLATERAL_PRODUCT',
  COLLATERAL_TRADING_TYPE = 'COLLATERAL_TRADING_TYPE',
  COLLATERAL_PRICE_CLEAN = 'COLLATERAL_PRICE_CLEAN',
  COLLATERAL_TICKET_SIZE = 'COLLATERAL_TICKET_SIZE',
  COLLATERAL_ISSUER = 'COLLATERAL_ISSUER',
  COLLATERAL_FIRST_SETTLEMENT_DATE = 'COLLATERAL_FIRST_SETTLEMENT_DATE',
  COLLATERAL_COUPON_ACCRUAL_DATE = 'COLLATERAL_COUPON_ACCRUAL_DATE',
  COLLATERAL_COUPON_FIRST_DATE = 'COLLATERAL_COUPON_FIRST_DATE',
  COLLATERAL_COUPON_MATURE_DATE = 'COLLATERAL_COUPON_MATURE_DATE',
  COLLATERAL_COUPON_TYPE = 'COLLATERAL_COUPON_TYPE',
  COLLATERAL_BASIS_CONVENTION = 'COLLATERAL_BASIS_CONVENTION',
  COLLATERAL_COUPON = 'COLLATERAL_COUPON',
  COLLATERAL_COUPON_FREQUENCY = 'COLLATERAL_COUPON_FREQUENCY',
  COLLATERAL_TYPE = 'COLLATERAL_TYPE',
  COLLATERAL_INDEX = 'COLLATERAL_INDEX',
  COLLATERAL_INDEX_LAG = 'COLLATERAL_INDEX_LAG',
  COLLATERAL_BASE_INDEX_VALUE = 'COLLATERAL_BASE_INDEX_VALUE'
}

function getTradingStatusColor(collateralDetail: PendingCollateralDetail) {
  if (collateralDetail.validationIssues && collateralDetail.validationIssues.length > 0) {
    return constants.STATUS_COLOR_INVALID
  } else {
    return getCollateralStatusColor(collateralDetail.collateral.status)
  }
}

export const PendingCollateralColumnDefs: ColDef[] = [
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_TRADING,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) =>
      params.data && getCollateralStatusActiveSuspendedText(params.data.collateral.status),
    headerName: Locale.translate(Translation.TC_TRADING),
    width: 120,
    equals: () => false,
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: PendingCollateralDetail) => getTradingStatusColor(data)
    },
    lockVisible: true,
    lockPosition: true
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_PLATFORM,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.collateralIdentifierType,
    headerName: Locale.translate(Translation.TC_PLATFORM),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.collateralIdentifierType'
    },
    valueFormatter: collateralIdentifierTypeFormatter
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_NAME,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.name,
    headerName: Locale.translate(Translation.TC_SHORT_ID),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.name'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_ISIN,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.isin,
    headerName: Locale.translate(Translation.TC_ISIN),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.isin'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_FISN,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.fisin,
    headerName: Locale.translate(Translation.TC_FISN),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.fisin'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_CFI,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.cfi,
    headerName: Locale.translate(Translation.TC_CFI),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.cfi'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_CUSIP,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.cusip,
    headerName: Locale.translate(Translation.TC_CUSIP),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.cusip'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_CURRENCY,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.currency,
    headerName: Locale.translate(Translation.TC_CURRENCY),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.currency'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUNTRY,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.countryCode,
    headerName: Locale.translate(Translation.TC_COUNTRY),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.countryCode'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_SECTOR,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.productSectorId'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_PRODUCT,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.product?.name,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 120
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_TRADING_TYPE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.tradingType,
    headerName: Locale.translate(Translation.TC_TRADING_TYPE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.tradingType'
    },
    valueFormatter: tradingTypeFormatter
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_PRICE_CLEAN,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateralPrice?.cleanPrice,
    headerName: Locale.translate(Translation.TC_CLEAN_PRICE),
    width: 120,
    type: 'numericColumn',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateralPrice.cleanPrice'
    },
    valueFormatter: makeNumberFormatter()
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_TICKET_SIZE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.ticketIncrement,
    headerName: Locale.translate(Translation.TC_TICKET_SIZE),
    width: 120,
    type: 'numericColumn',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.ticketIncrement'
    },
    valueFormatter: makeNumberFormatter()
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_ISSUER,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.issuer,
    headerName: Locale.translate(Translation.TC_ISSUER),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.issuer'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_FIRST_SETTLEMENT_DATE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.issueDate,
    headerName: Locale.translate(Translation.TC_FIRST_SETTLEMENT_DATE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.issueDate'
    },
    valueFormatter: makeDateFormatter()
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUPON_ACCRUAL_DATE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.couponAccruedInterestDate,
    headerName: Locale.translate(Translation.TC_ACCRUAL_DATE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.couponAccruedInterestDate'
    },
    valueFormatter: makeDateFormatter()
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUPON_FIRST_DATE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.couponFirstDate,
    headerName: Locale.translate(Translation.TC_FIRST_COUPON_DATE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.couponFirstDate'
    },
    valueFormatter: makeDateFormatter()
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUPON_MATURE_DATE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.couponMaturityDate,
    headerName: Locale.translate(Translation.TC_MATURITY_DATE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.couponMaturityDate'
    },
    valueFormatter: makeDateFormatter()
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUPON_TYPE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.couponType,
    headerName: Locale.translate(Translation.TC_COUPON_TYPE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.couponType'
    },
    valueFormatter: couponTypeFormatter
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_BASIS_CONVENTION,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.basisConvention,
    headerName: Locale.translate(Translation.TC_BASIS_CONVENTION),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.basisConvention'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUPON,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.coupon,
    headerName: Locale.translate(Translation.TC_COUPON),
    width: 120,
    type: 'numericColumn',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.coupon'
    },
    valueFormatter: makeNumberFormatter({ percent: true, minDp: 1, decimalShift: 2 })
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_COUPON_FREQUENCY,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.couponFrequency,
    headerName: Locale.translate(Translation.TC_FREQUENCY),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.couponFrequency'
    },
    valueFormatter: couponFrequencyFormatter
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_TYPE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.collateralType,
    headerName: Locale.translate(Translation.TC_COLLATERAL_TYPE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.collateralType'
    },
    valueFormatter: collateralTypeFormatter
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_INDEX,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.index?.code,
    headerName: Locale.translate(Translation.TC_INDEX),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.collateralIndexDetails.indexId'
    }
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_INDEX_LAG,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) => params.data?.collateral.collateralIndexDetails?.indexLag,
    headerName: Locale.translate(Translation.TC_INDEX_LAG),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.collateralIndexDetails.indexLag'
    },
    valueFormatter: indexLagFormatter
  },
  {
    colId: PendingCollateralGridColumnId.COLLATERAL_BASE_INDEX_VALUE,
    valueGetter: (params: TypedValueGetterParams<PendingCollateralDetail>) =>
      params.data?.collateral.collateralIndexDetails?.baseIndexValue,
    headerName: Locale.translate(Translation.TC_BASE_INDEX_VALUE),
    width: 120,
    type: 'numericColumn',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'collateral.collateralIndexDetails.baseIndexValue'
    },
    valueFormatter: makeNumberFormatter({ minDp: 6 })
  }
]

export const pendingCollateralGridOptions: GridOptions<PendingCollateralDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.collateral.id
}
